import React, { forwardRef, useMemo, useState } from 'react';
import FloatingMenu from '../../../mp/components/common/FloatingMenu';
import { IconButton } from '../../button';
import { JobListing } from '../../../redux/rtk';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  copyJob,
  hideModal,
  listProjects,
  showModal,
} from '../../../redux/actions';
import { INVITE_PROJECT_USERS_MODAL } from '../../modal/inviteProjectUsersModal/InviteProjectUsersModal';
import text from '../../../text';
import { profileSelectors } from '../../../redux/selectors/profile';
import { useHistory } from 'react-router-dom';
import { DELETE_PROJECT_MODAL } from '../../modal/DeleteProjectModal';
import routes from '../../../routes/constants';
import { MERGE_PROJECT_MODAL } from '../../modal/MergeProjectModal';

interface OrderListingMenuProps {
  job: JobListing;
}

const OrderListingMenu = forwardRef<HTMLDivElement, OrderListingMenuProps>(
  ({ job }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useDispatch();

    const isAdmin = useSelector(profileSelectors.getIsAdmin);
    const canDeleteAnyProject = useSelector(
      profileSelectors.getCanDeleteAnyProject
    );
    const history = useHistory();

    const onEdit = () => {
      history.push(`/mp/${job.map2d_id}`);
    };

    const onCopy = () => {
      dispatch(copyJob(job.id, isAdmin));
    };

    const handleDeleteProject = () =>
      dispatch(
        showModal(DELETE_PROJECT_MODAL, {
          projectId: job?.projectId,
          name: job?.project.name,
        })
      );

    const handleAddOrderToProject = () =>
      history.push({
        pathname: routes.mp.root,
        search: `?projectId=${job?.project?.id}`,
      });

    const handleMergeProject = () =>
      dispatch(
        showModal(MERGE_PROJECT_MODAL, {
          currentJob: job,
          onSuccess: () => dispatch(listProjects(isAdmin)),
        })
      );

    const isDeleteEnabled = useMemo(
      () =>
        job.status !== 'demo' &&
        (job?.jobs?.every((item) => item.status == 'draft') ||
          canDeleteAnyProject),
      [job, canDeleteAnyProject]
    );

    return (
      <div ref={ref} className='oder-listing-menu'>
        <FloatingMenu
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          placement='bottom-end'
          offset={{ mainAxis: 8, crossAxis: 12 }}
          arrowConfig={{
            fill: '#ffffff',
            stroke: '#dcdcdc',
            strokeWidth: 1,
            width: 15,
            height: 8,
          }}
          className='oder-listing-menu-floating'
          innerClassName='oder-listing-menu-floating-inner'
          containerSelector='.oder-listing-menu-floating-inner'
          trigger={<IconButton icon='more' className='table-icon-button' />}
        >
          <Dropdown.Item
            disabled={['demo'].includes(job.status)}
            onClick={() =>
              dispatch(
                showModal(INVITE_PROJECT_USERS_MODAL, {
                  onHide: () => dispatch(hideModal(INVITE_PROJECT_USERS_MODAL)),
                  project: job?.project,
                  job: job,
                })
              )
            }
          >
            {text('inviteViewProjectUsers')}
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!['draft'].includes(job.status)}
            onClick={onEdit}
          >
            Edit
          </Dropdown.Item>
          {job.status !== 'demo' && (
            <>
              <Dropdown.Item onClick={onCopy}>Duplicate</Dropdown.Item>
              <Dropdown.Item onClick={handleAddOrderToProject}>
                {text('addNewOrder')}
              </Dropdown.Item>
              <Dropdown.Item onClick={handleMergeProject}>
                {text('mergeProjects')}
              </Dropdown.Item>
            </>
          )}
          <Dropdown.Item
            disabled={!isDeleteEnabled}
            onClick={handleDeleteProject}
          >
            {text('deleteProject')}
          </Dropdown.Item>
        </FloatingMenu>
      </div>
    );
  }
);

OrderListingMenu.displayName = 'OrderListingMenu';

export default OrderListingMenu;
